import type { RouteRecordRaw } from 'vue-router';
import { CompanyPermissionsCodename, PermissionsCodename } from 'src/api';
import { requireUserPermission } from 'src/router/guards/requireUserPermission';
import { requireCompanyPermission } from 'src/router/guards/requireCompanyPermission';
import gettext from 'src/utils/gettext';

const { $gettext } = gettext;

export const visitsActionRoutes: RouteRecordRaw[] = [
  {
    path: 'add-leave',
    alias: $gettext('pridat-volno'),
    name: 'create-time-off',
    props: (route) => ({
      userId: route.query.userId,
      timeOffDate: route.query.timeOffDate,
      timeOffDateEnd: route.query.timeOffDateEnd,
      calendarSelectedDate: route.query.calendarSelectedDate,
      next: route.query.next,
    }),
    component: () => import('pages/office/CreateTimeOffPage.vue'),
  },
  {
    path: 'edit-leave',
    alias: $gettext('upravit-volno'),
    name: 'update-time-off',
    props: (route) => ({
      userId: route.query.userId,
      timeOffId: route.query.timeOffId,
      calendarSelectedDate: route.query.calendarSelectedDate,
      next: route.query.next,
    }),
    component: () => import('pages/office/UpdateTimeOffPage.vue'),
  },
  {
    path: 'new-visit',
    alias: $gettext('nova-navsteva'),
    name: 'new-visit',
    props: (route) => ({
      userId: route.query.userId,
      patientId: route.query.patientId,
      time: route.query.time,
      next: route.query.next,
      isFromCreatePatient: route.query.isFromCreatePatient,
      initialRoomId: route.query.initialRoomId,
      unfinishedVisit: route.query.unfinishedVisit === 'true',
    }),
    component: () => import('src/pages/VisitCreatePage.vue'),
  },
  {
    path: 'new-group-visit',
    alias: $gettext('nova-skupinova-navsteva'),
    name: 'new-group-visit',
    props: (route) => ({
      userId: route.query.userId,
      time: route.query.time,
      next: route.query.next,
      initialRoomId: route.query.initialRoomId,
    }),
    component: () => import('src/pages/group-visit/GroupVisitCreatePage.vue'),
  },
];

// Mobile route tree mirrors the web canonical paths/names/aliases (web is the
// source of truth). The two trees never coexist in one build, so reusing web
// route names is safe. Sections below are mounted under the single MobileLayout
// in routes.mobile.ts; paths compose to the same URLs as web.
export const mobileVisitsRoutes: RouteRecordRaw[] = [
  // Návštěvy day-agenda is the mobile home. Shares the `home` name/URL with web
  // (web `home` is the Dashboard — content differs, URL is shared by design).
  {
    path: 'home',
    alias: $gettext('domu'),
    name: 'home',
    meta: { bottomNav: true },
    // The agenda is a calendar day view, so a package without the calendar
    // (START) has no agenda either; denied `home` lands on `patients`.
    beforeEnter: [
      requireUserPermission(PermissionsCodename.LIST_VISITS),
      requireCompanyPermission(CompanyPermissionsCodename.ALLOW_CALENDAR),
    ],
    component: () => import('pages/mobile/MobileVisitsPage.vue'),
  },
  // New visit / new group visit → web `/actions` (`/akce`) section.
  {
    path: 'actions',
    alias: $gettext('akce'),
    children: [
      {
        path: 'new-visit',
        alias: $gettext('nova-navsteva'),
        name: 'new-visit',
        props: (route) => ({ patientId: route.query.patientId }),
        beforeEnter: requireUserPermission(PermissionsCodename.CREATE_VISIT),
        component: () => import('pages/mobile/MobileNewVisitPage.vue'),
      },
      {
        path: 'new-group-visit',
        alias: $gettext('nova-skupinova-navsteva'),
        name: 'new-group-visit',
        beforeEnter: requireUserPermission(
          PermissionsCodename.CREATE_GROUP_VISIT
        ),
        component: () => import('pages/mobile/MobileNewGroupVisitPage.vue'),
      },
      // Reuses the web path/name (`add-leave` / `create-time-off`) so both trees
      // address the time-off form identically.
      // No guard on purpose: own leave is always allowed, and the colleague
      // case depends on the `userId` query param, which a route guard cannot
      // see before the page loads. MobileNewTimeOffPage.vue enforces it: on
      // mount the create path forces `userId` back to the signed-in user when
      // CREATE_COLLEAGUE_TIME_OFF is missing, and `submit()` re-checks the same
      // rule before calling `createUserTimeOff`.
      {
        path: 'add-leave',
        alias: $gettext('pridat-volno'),
        name: 'create-time-off',
        props: (route) => ({ userId: route.query.userId }),
        component: () => import('pages/mobile/MobileNewTimeOffPage.vue'),
      },
      // Same form in edit mode, same path/name as the web route.
      // No guard on purpose: own leave is always allowed, and the colleague
      // case depends on the `userId` query param, which a route guard cannot
      // see before the page loads. MobileNewTimeOffPage.vue enforces it: the
      // edit path's `onMounted` sends the user back when a colleague's volno is
      // opened without UPDATE_COLLEAGUE_TIME_OFF, and the delete action is
      // gated on DELETE_COLLEAGUE_TIME_OFF.
      {
        path: 'edit-leave',
        alias: $gettext('upravit-volno'),
        name: 'update-time-off',
        props: (route) => ({
          timeOffId: route.query.timeOffId,
          userId: route.query.userId,
        }),
        component: () => import('pages/mobile/MobileNewTimeOffPage.vue'),
      },
    ],
  },
  // Group-visit edit / add-client → web `/group-visits` (`/skupinove-navstevy`)
  // section. Mobile-only leaves (web has no such screens).
  {
    path: 'group-visits',
    alias: $gettext('skupinove-navstevy'),
    children: [
      {
        path: ':groupVisitId/edit',
        alias: ':groupVisitId/' + $gettext('upravit'),
        name: 'edit-group-visit',
        props: true,
        beforeEnter: requireUserPermission(
          PermissionsCodename.UPDATE_GROUP_VISIT
        ),
        component: () => import('pages/mobile/MobileEditGroupVisitPage.vue'),
      },
      {
        path: ':groupVisitId/add-client',
        alias: ':groupVisitId/' + $gettext('pridat-klienta'),
        name: 'add-group-visit-client',
        props: true,
        beforeEnter: requireUserPermission(
          PermissionsCodename.UPDATE_GROUP_VISIT
        ),
        component: () =>
          import('pages/mobile/MobileAddGroupVisitClientPage.vue'),
      },
    ],
  },
  // Visit sub-pages are mobile-only (web nests these under patient visits).
  // Hosted under a mobile-only `/visits` namespace — documented parity gap.
  {
    path: 'visits',
    alias: $gettext('navstevy'),
    children: [
      {
        path: ':visitId/edit',
        alias: ':visitId/' + $gettext('upravit'),
        name: 'edit-visit',
        props: true,
        beforeEnter: requireUserPermission(PermissionsCodename.UPDATE_VISIT),
        component: () => import('pages/mobile/MobileEditVisitPage.vue'),
      },
      {
        path: ':visitId/popis',
        name: 'visit-description',
        props: true,
        beforeEnter: requireUserPermission(
          PermissionsCodename.GET_VISIT_DETAIL
        ),
        component: () => import('pages/mobile/MobileVisitDescriptionPage.vue'),
      },
      {
        path: ':visitId/zakres',
        name: 'visit-drawing',
        props: true,
        beforeEnter: requireUserPermission(
          PermissionsCodename.GET_VISIT_DETAIL
        ),
        component: () => import('pages/mobile/MobileVisitDrawingPage.vue'),
      },
      {
        path: ':visitId/foto',
        name: 'visit-photos',
        props: true,
        beforeEnter: requireUserPermission(
          PermissionsCodename.LIST_VISIT_IMAGES
        ),
        component: () => import('pages/mobile/MobileVisitPhotosPage.vue'),
      },
      {
        path: ':visitId/dokumenty',
        name: 'visit-documents',
        props: true,
        beforeEnter: requireUserPermission(
          PermissionsCodename.LIST_VISIT_FORMS
        ),
        component: () => import('pages/mobile/MobileVisitDocumentsPage.vue'),
      },
    ],
  },
];
